<template>
  <div class="p-6">
    <div class="flex justify-between items-center mb-6">
      <h1 class="text-2xl font-bold text-gray-600 dark:text-gray-100">Workers Filter</h1>
      <button
        class="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors duration-200 flex items-center space-x-2 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-400"
        @click="showCreateModal = true"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          class="h-5 w-5"
          viewBox="0 0 20 20"
          fill="currentColor"
        >
          <path
            fill-rule="evenodd"
            d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z"
            clip-rule="evenodd"
          />
        </svg>
        <span>Create Filter</span>
      </button>
    </div>

    <!-- Subscription Input with Typeahead and Search Box in same row -->
    <div class="mb-6 flex items-start space-x-6">
      <!-- Subscription Input -->
      <div class="w-1/2">
        <label
          for="subscription"
          class="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300"
          >Subscription Name</label
        >
        <div class="subscription-input-container relative">
          <div class="mt-1 relative">
            <input
              id="subscription"
              v-model="subscriptionName"
              type="text"
              class="w-full px-3 py-2 rounded-md border focus:outline-none focus:ring-1 focus:ring-blue-400 focus:border-blue-400 sm:text-sm bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-100"
              placeholder="e.g., crm-conversations-ai-inbound-events-sub"
              autocomplete="off"
              spellcheck="false"
              aria-autocomplete="none"
              @focus="showSuggestions = true"
              @blur="handleInputBlur"
              @keydown="handleKeydown"
            />
            <!-- Clear button -->
            <button
              v-if="subscriptionName.trim()"
              type="button"
              class="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-400"
              title="Clear subscription"
              @click="clearSubscription"
              @mousedown.prevent
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="h-5 w-5"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fill-rule="evenodd"
                  d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                  clip-rule="evenodd"
                />
              </svg>
            </button>
          </div>

          <!-- Suggestions Dropdown -->
          <div
            v-if="showSuggestions && (subscriptionSuggestions.length > 0 || isLoadingSuggestions)"
            class="absolute z-50 mt-1 w-full bg-white dark:bg-gray-700 rounded-md shadow-lg border border-gray-200 dark:border-gray-600"
          >
            <div class="py-1">
              <!-- Loading State -->
              <div
                v-if="isLoadingSuggestions"
                class="px-3 py-2 text-sm text-gray-500 dark:text-gray-400"
              >
                <div class="flex items-center">
                  <svg
                    class="animate-spin h-4 w-4 mr-2 text-blue-500"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      class="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      stroke-width="4"
                    ></circle>
                    <path
                      class="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Loading suggestions...
                </div>
              </div>

              <!-- Suggestions List -->
              <template v-else>
                <button
                  v-for="suggestion in subscriptionSuggestions"
                  :key="suggestion"
                  class="w-full text-left px-3 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-600 hover:text-gray-900 dark:hover:text-white focus:outline-none focus:bg-gray-100 dark:focus:bg-gray-600 focus:text-gray-900 dark:focus:text-white"
                  @click="handleSuggestionClick(suggestion)"
                >
                  {{ suggestion }}
                </button>
              </template>
            </div>
          </div>
        </div>
      </div>

      <!-- Search Box -->
      <div class="w-1/2">
        <label
          for="search"
          class="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300"
        >
          Search Routes
          <span
            v-if="!workerFilters.length"
            class="text-xs text-gray-500 dark:text-gray-400 ml-1"
          >
            (Select a subscription first)
          </span>
        </label>
        <div class="relative">
          <input
            id="search"
            ref="searchInput"
            v-model="searchQuery"
            type="text"
            :disabled="!workerFilters.length"
            :class="[
              'w-full pl-10 pr-4 py-2 border rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500',
              workerFilters.length
                ? 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100'
                : 'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-400 dark:text-gray-500 cursor-not-allowed'
            ]"
            :placeholder="
              workerFilters.length
                ? 'Search all fields (topic, author, integrations, match conditions)...'
                : 'Select a subscription to enable search'
            "
          />
          <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <svg
              class="h-5 w-5"
              :class="
                workerFilters.length
                  ? 'text-gray-400 dark:text-gray-500'
                  : 'text-gray-300 dark:text-gray-600'
              "
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
          </div>
        </div>
      </div>
    </div>

    <!-- Loading State -->
    <div
      v-if="isLoading"
      class="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-100 dark:border-gray-700 p-6"
    >
      <div class="flex items-center justify-center py-12">
        <div
          class="w-8 h-8 border-4 border-blue-600 rounded-full border-t-transparent animate-spin"
        ></div>
        <span class="ml-3 text-gray-600 dark:text-gray-400">Loading filters...</span>
      </div>
    </div>

    <!-- Error State -->
    <div
      v-else-if="error"
      class="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-red-100 dark:border-red-800 p-6"
    >
      <div class="flex items-center justify-center py-12 text-red-600 dark:text-red-400">
        <span>{{ error }}</span>
      </div>
    </div>

    <!-- Content -->
    <div
      v-else
      class="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-100 dark:border-gray-700/90 overflow-hidden"
    >
      <div
        v-if="paginatedData.length === 0"
        class="p-6 text-center text-gray-600 dark:text-gray-400"
      >
        No filters found for this subscription.
      </div>
      <div
        v-else
        class="overflow-x-auto"
      >
        <table class="min-w-full divide-y divide-gray-200 dark:divide-gray-700/30">
          <thead class="bg-gray-50 dark:bg-gray-800/80 rounded-t-xl">
            <tr>
              <th
                v-for="column in columns"
                :key="column.field"
                class="px-6 py-3 text-left text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wider first:rounded-tl-xl last:rounded-tr-xl"
                :class="{ 'cursor-pointer': column.sortable }"
                @click="column.sortable && handleSort(column.field)"
              >
                <div class="flex items-center space-x-1">
                  <span>{{ column.label }}</span>
                  <span
                    v-if="column.sortable"
                    class="flex flex-col"
                  >
                    <svg
                      :class="[
                        'w-3 h-3 transform',
                        sortField === column.field && sortOrder === 'asc'
                          ? 'text-blue-600 dark:text-blue-400'
                          : 'text-gray-400 dark:text-gray-500'
                      ]"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M5 15l7-7 7 7"
                      />
                    </svg>
                    <svg
                      :class="[
                        'w-3 h-3 transform',
                        sortField === column.field && sortOrder === 'desc'
                          ? 'text-blue-600 dark:text-blue-400'
                          : 'text-gray-400 dark:text-gray-500'
                      ]"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </span>
                </div>
              </th>
            </tr>
          </thead>
          <tbody
            class="bg-white dark:bg-gray-800/90 divide-y divide-gray-200 dark:divide-gray-700/30"
          >
            <template
              v-for="item in paginatedData"
              :key="item._id"
            >
              <!-- Main Row -->
              <tr
                class="hover:bg-gray-50 dark:hover:bg-gray-700/70 cursor-pointer transition-colors duration-200"
                @click="toggleExpand(item._id)"
              >
                <td class="px-6 py-4 whitespace-nowrap">
                  <div class="text-sm font-medium text-gray-600 dark:text-gray-100">
                    {{ item.developerTopic }}
                  </div>
                </td>
                <td class="px-6 py-4 whitespace-nowrap">
                  <div class="text-sm text-gray-600 dark:text-gray-400">
                    {{ item.addedBy || '-' }}
                  </div>
                </td>
                <td class="px-6 py-4 whitespace-nowrap">
                  <div class="text-sm text-gray-600 dark:text-gray-400">
                    <span
                      :class="[
                        item.isIntegrations
                          ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                          : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300',
                        'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium'
                      ]"
                    >
                      {{ item.isIntegrations ? 'Yes' : 'No' }}
                    </span>
                  </div>
                </td>
                <td class="px-6 py-4">
                  <div class="text-sm text-gray-600 dark:text-gray-300 font-mono">
                    {{
                      columns
                        .find((col) => col.field === 'matchCondition')
                        ?.formatter?.(item.matchCondition)
                    }}
                  </div>
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400">
                  {{ formatDate(item.createdAt) }}
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400">
                  {{ formatDate(item.updatedAt) }}
                </td>
                <!-- Actions Column -->
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400">
                  <div class="flex items-center space-x-3">
                    <button
                      class="text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 transition-colors"
                      title="Edit filter"
                      @click="handleEditClick(item, $event)"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        class="h-5 w-5"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z"
                        />
                      </svg>
                    </button>
                    <button
                      class="text-rose-500 dark:text-red-400 hover:text-rose-600 dark:hover:text-red-300 transition-colors"
                      title="Delete filter"
                      @click="handleDeleteClick(item._id, $event)"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        class="h-5 w-5"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fill-rule="evenodd"
                          d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z"
                          clip-rule="evenodd"
                        />
                      </svg>
                    </button>
                  </div>
                </td>
              </tr>
              <!-- Expanded Row -->
              <tr
                v-if="expandedRows.has(item._id)"
                class="bg-gray-50 dark:bg-gray-700/90"
              >
                <td
                  colspan="6"
                  class="px-6 py-4"
                >
                  <div class="grid grid-cols-1 gap-4">
                    <!-- Match Condition -->
                    <div
                      v-if="Object.keys(item.matchCondition || {}).length"
                      class="space-y-2"
                    >
                      <h4 class="font-medium text-indigo-700 dark:text-indigo-400">
                        Match Condition
                      </h4>
                      <pre
                        class="bg-white dark:bg-gray-800 p-4 rounded border border-indigo-200 dark:border-indigo-700 text-sm overflow-auto max-h-60 font-mono text-gray-800 dark:text-gray-200"
                        >{{ JSON.stringify(item.matchCondition, null, 2) }}</pre
                      >
                      <p class="text-xs text-gray-500 dark:text-gray-400 mt-2">
                        This condition is applied to the incoming payload. When matched, events are
                        forwarded from the default subscription ({{ item.subscription }}) to your
                        developer topic.
                      </p>
                    </div>
                  </div>
                </td>
              </tr>
            </template>
          </tbody>
        </table>
      </div>
      <!-- Pagination -->
      <div
        v-if="paginatedData.length > 0 && totalPages > 1"
        :class="[
          theme.isDark.value ? 'border-gray-700' : 'border-gray-200',
          'px-4 py-3 border-t sm:px-6 flex items-center justify-between'
        ]"
      >
        <div class="flex-1 flex justify-between sm:hidden">
          <button
            :disabled="currentPage === 1"
            :class="[
              theme.isDark.value
                ? 'bg-gray-700 border-gray-600 text-gray-300 hover:bg-gray-600'
                : 'bg-white border-gray-300 text-gray-600 hover:bg-gray-50',
              'relative inline-flex items-center px-4 py-2 border text-sm font-medium rounded-md'
            ]"
            @click="currentPage--"
          >
            Previous
          </button>
          <button
            :disabled="currentPage === totalPages"
            :class="[
              theme.isDark.value
                ? 'bg-gray-700 border-gray-600 text-gray-300 hover:bg-gray-600'
                : 'bg-white border-gray-300 text-gray-600 hover:bg-gray-50',
              'ml-3 relative inline-flex items-center px-4 py-2 border text-sm font-medium rounded-md'
            ]"
            @click="currentPage++"
          >
            Next
          </button>
        </div>
        <div class="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
          <div>
            <p :class="[theme.isDark.value ? 'text-gray-300' : 'text-gray-600', 'text-sm']">
              Showing
              <span class="font-medium">{{ (currentPage - 1) * itemsPerPage + 1 }}</span>
              to
              <span class="font-medium">{{
                Math.min(currentPage * itemsPerPage, filteredData.length)
              }}</span>
              of
              <span class="font-medium">{{ filteredData.length }}</span>
              results
            </p>
          </div>
          <div>
            <nav
              class="relative z-0 inline-flex rounded-md shadow-sm -space-x-px"
              aria-label="Pagination"
            >
              <button
                :disabled="currentPage === 1"
                :class="[
                  theme.isDark.value
                    ? 'bg-gray-700 border-gray-600 text-gray-300 hover:bg-gray-600'
                    : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50',
                  'relative inline-flex items-center px-2 py-2 rounded-l-md border text-sm font-medium'
                ]"
                @click="currentPage = 1"
              >
                <span class="sr-only">First</span>
                ⟪
              </button>
              <button
                :disabled="currentPage === 1"
                :class="[
                  theme.isDark.value
                    ? 'bg-gray-700 border-gray-600 text-gray-300 hover:bg-gray-600'
                    : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50',
                  'relative inline-flex items-center px-2 py-2 border text-sm font-medium'
                ]"
                @click="currentPage--"
              >
                <span class="sr-only">Previous</span>
                ⟨
              </button>
              <template
                v-for="page in displayedPages"
                :key="page"
              >
                <button
                  v-if="page !== '...'"
                  :class="[
                    currentPage === page
                      ? theme.isDark.value
                        ? 'z-10 bg-blue-900 border-blue-600 text-blue-300'
                        : 'z-10 bg-blue-50 border-blue-400 text-blue-500'
                      : theme.isDark.value
                        ? 'bg-gray-700 border-gray-600 text-gray-300 hover:bg-gray-600'
                        : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50',
                    'relative inline-flex items-center px-4 py-2 border text-sm font-medium'
                  ]"
                  @click="currentPage = Number(page)"
                >
                  {{ page }}
                </button>
                <span
                  v-else
                  :class="[
                    theme.isDark.value
                      ? 'border-gray-600 bg-gray-700 text-gray-400'
                      : 'border-gray-300 bg-white text-gray-700',
                    'relative inline-flex items-center px-4 py-2 border text-sm font-medium'
                  ]"
                >
                  ...
                </span>
              </template>
              <button
                :disabled="currentPage === totalPages"
                :class="[
                  theme.isDark.value
                    ? 'bg-gray-700 border-gray-600 text-gray-300 hover:bg-gray-600'
                    : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50',
                  'relative inline-flex items-center px-2 py-2 border text-sm font-medium'
                ]"
                @click="currentPage++"
              >
                <span class="sr-only">Next</span>
                ⟩
              </button>
              <button
                :disabled="currentPage === totalPages"
                :class="[
                  theme.isDark.value
                    ? 'bg-gray-700 border-gray-600 text-gray-300 hover:bg-gray-600'
                    : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50',
                  'relative inline-flex items-center px-2 py-2 rounded-r-md border text-sm font-medium'
                ]"
                @click="currentPage = totalPages"
              >
                <span class="sr-only">Last</span>
                ⟫
              </button>
            </nav>
          </div>
        </div>
      </div>
    </div>

    <!-- Edit Modal -->
    <CreateWorkerFilterModal
      v-if="showEditModal"
      v-model="showEditModal"
      :filter-data="filterToEdit"
      :edit-mode="true"
      @updated="handleEditSubmit"
    />

    <!-- Toast Notification -->
    <Toast
      v-model:show="toast.show"
      :message="toast.message"
      :type="toast.type"
      :details="toast.details"
      :duration="5000"
    />

    <!-- Delete Confirmation Modal -->
    <div
      v-if="showDeleteConfirm"
      class="fixed inset-0 z-50 overflow-y-auto"
      aria-labelledby="modal-title"
      role="dialog"
      aria-modal="true"
    >
      <div
        class="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0"
      >
        <div
          class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
          aria-hidden="true"
        ></div>
        <div
          class="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full"
        >
          <div class="bg-white dark:bg-gray-800 px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div class="sm:flex sm:items-start">
              <div
                class="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-rose-100 dark:bg-red-900 sm:mx-0 sm:h-10 sm:w-10"
              >
                <svg
                  class="h-6 w-6 text-rose-500 dark:text-red-400"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                  />
                </svg>
              </div>
              <div class="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                <h3 class="text-lg leading-6 font-medium text-gray-900 dark:text-gray-100">
                  Delete Filter
                </h3>
                <div class="mt-2">
                  <p class="text-sm text-gray-500 dark:text-gray-400">
                    Are you sure you want to delete this filter? This action cannot be undone.
                  </p>
                </div>
              </div>
            </div>
          </div>
          <div
            class="bg-gray-50 dark:bg-gray-700 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse gap-3"
          >
            <button
              type="button"
              class="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-6 py-2.5 bg-rose-500 dark:bg-red-600 text-base font-medium text-white hover:bg-rose-600 dark:hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-rose-500 dark:focus:ring-red-500 transition-colors duration-200 sm:w-auto sm:text-sm"
              @click="handleDelete"
            >
              Delete
            </button>
            <button
              type="button"
              class="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 dark:border-gray-600 shadow-sm px-6 py-2.5 bg-white dark:bg-gray-800 text-base font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-400 sm:mt-0 sm:w-auto sm:text-sm"
              @click="showDeleteConfirm = false"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Create Modal -->
    <CreateWorkerFilterModal
      v-model="showCreateModal"
      :subscription="subscriptionName"
      @created="handleCreated"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, reactive, onUnmounted, inject, onBeforeMount } from 'vue'
import { packagesApiClient } from '../../services/api/packagesApiClient'
import type { WorkerFilter } from '../../types/workers-filter'
import CreateWorkerFilterModal from '../../components/features/workers-filter/CreateFilterModal.vue'
import Toast from '../../components/base/common/Toast.vue'
import { useTheme } from '../../utils/useTheme'
// Remove unused useRoute import

// Use theme via inject with a fallback
const theme = inject('theme', useTheme())
// Remove unused route import

// Data
const workerFilters = ref<WorkerFilter[]>([])
const isLoading = ref(false)
const error = ref<string | null>(null)
const expandedRows = ref<Set<string>>(new Set())
const subscriptionName = ref('')
const searchQuery = ref('')

// Sorting
const sortField = ref('createdAt')
const sortOrder = ref<'asc' | 'desc'>('desc')

// Pagination
const currentPage = ref(1)
const itemsPerPage = ref(10)

// Table configuration
const columns = [
  {
    field: 'developerTopic',
    label: 'Developer Topic',
    sortable: true,
    searchable: true
  },
  {
    field: 'addedBy',
    label: 'Added By',
    sortable: true,
    searchable: true
  },
  {
    field: 'isIntegrations',
    label: 'IS INTEROP',
    sortable: true,
    formatter: (value: any) => {
      return value ? 'Yes' : 'No'
    }
  },
  {
    field: 'matchCondition',
    label: 'Match Condition',
    sortable: false,
    formatter: (value: Record<string, any> | null | undefined) => {
      if (!value || Object.keys(value).length === 0) return '-'
      const jsonStr = JSON.stringify(value)
      return jsonStr.length > 50 ? `${jsonStr.substring(0, 47)}...` : jsonStr
    }
  },
  {
    field: 'createdAt',
    label: 'Created',
    sortable: true,
    type: 'date'
  },
  {
    field: 'updatedAt',
    label: 'Updated',
    sortable: true,
    type: 'date'
  },
  {
    field: 'actions',
    label: 'Actions',
    sortable: false
  }
]

// Add this helper function for deep search
const deepSearch = (obj: any, searchText: string): boolean => {
  if (!obj) return false

  // Convert to lower case for case-insensitive search
  searchText = searchText.toLowerCase()

  // If it's a string, check if it contains the search text
  if (typeof obj === 'string') {
    return obj.toLowerCase().includes(searchText)
  }

  // If it's a number or boolean, convert to string and check
  if (typeof obj === 'number' || typeof obj === 'boolean') {
    return String(obj).toLowerCase().includes(searchText)
  }

  // If it's an array, search through all elements
  if (Array.isArray(obj)) {
    return obj.some((item) => deepSearch(item, searchText))
  }

  // If it's an object, search through all values
  if (typeof obj === 'object') {
    return Object.values(obj).some((value) => deepSearch(value, searchText))
  }

  return false
}

// Update the filteredData computed property
const filteredData = computed(() => {
  let result = [...workerFilters.value]

  // Apply search
  if (searchQuery.value) {
    const query = searchQuery.value.toLowerCase()
    result = result.filter((item) => {
      // Deep search through the entire item object
      return deepSearch(item, query)
    })
  }

  // Apply sorting
  if (sortField.value) {
    result.sort((a, b) => {
      const aVal = a[sortField.value as keyof WorkerFilter]
      const bVal = b[sortField.value as keyof WorkerFilter]

      // Handle undefined values
      if (aVal === undefined && bVal === undefined) return 0
      if (aVal === undefined) return 1
      if (bVal === undefined) return -1

      // Handle dates
      if (sortField.value === 'createdAt' || sortField.value === 'updatedAt') {
        const aDate = new Date(aVal as string).getTime()
        const bDate = new Date(bVal as string).getTime()
        const comparison = aDate > bDate ? 1 : -1
        return sortOrder.value === 'asc' ? comparison : -comparison
      }

      // Handle other fields
      const comparison = String(aVal) > String(bVal) ? 1 : -1
      return sortOrder.value === 'asc' ? comparison : -comparison
    })
  }

  return result
})

const totalPages = computed(() => Math.ceil(filteredData.value.length / itemsPerPage.value))

const paginatedData = computed(() => {
  const start = (currentPage.value - 1) * itemsPerPage.value
  const end = start + itemsPerPage.value
  return filteredData.value.slice(start, end)
})

const displayedPages = computed(() => {
  const total = totalPages.value
  const current = currentPage.value
  const range: (number | string)[] = []

  if (total <= 7) {
    for (let i = 1; i <= total; i++) range.push(i)
  } else {
    if (current <= 3) {
      for (let i = 1; i <= 5; i++) range.push(i)
      range.push('...', total)
    } else if (current >= total - 2) {
      range.push(1, '...')
      for (let i = total - 4; i <= total; i++) range.push(i)
    } else {
      range.push(1, '...')
      for (let i = current - 1; i <= current + 1; i++) range.push(i)
      range.push('...', total)
    }
  }

  return range
})

// Methods
const handleSort = (field: string) => {
  if (sortField.value === field) {
    sortOrder.value = sortOrder.value === 'asc' ? 'desc' : 'asc'
  } else {
    sortField.value = field
    sortOrder.value = 'asc'
  }
}

const formatDate = (dateString: string | undefined): string => {
  if (!dateString) return ''
  return new Date(dateString).toLocaleString()
}

const toggleExpand = (id: string) => {
  if (expandedRows.value.has(id)) {
    expandedRows.value.delete(id)
  } else {
    expandedRows.value.add(id)
  }
}

// Watch for search changes to reset pagination
watch(searchQuery, () => {
  currentPage.value = 1
  expandedRows.value.clear()
})

// Create a ref for the search input
const searchInput = ref<HTMLInputElement | null>(null)

// Update the fetchFilters function to focus the search input when data is loaded
const fetchFilters = async () => {
  if (!subscriptionName.value.trim()) {
    error.value = 'Please enter a subscription name'
    return
  }

  try {
    isLoading.value = true
    error.value = null
    const response = await packagesApiClient.get<WorkerFilter[]>(
      `/platform/workers/version-configs?subscription=${encodeURIComponent(subscriptionName.value.trim())}`
    )
    workerFilters.value = response

    // Focus the search input after data is loaded
    if (response.length > 0 && searchInput.value) {
      setTimeout(() => {
        searchInput.value?.focus()
      }, 100)
    }
  } catch (err) {
    error.value = 'Failed to load worker filters'
  } finally {
    isLoading.value = false
  }
}

// Lifecycle
onMounted(() => {
  // Only add event listener here, data is loaded in onBeforeMount
  document.addEventListener('click', handleClickOutside)
})

// Toast state
const toast = reactive({
  show: false,
  message: '',
  type: 'success' as 'success' | 'error',
  details: [] as string[]
})

const showToast = (message: string, type: 'success' | 'error', details?: string[]) => {
  toast.message = message
  toast.type = type
  toast.details = details || []
  toast.show = true
}

// Create Modal State
const showCreateModal = ref(false)

const handleCreated = () => {
  fetchFilters()
  showToast('Worker filter created successfully', 'success')
}

// Delete confirmation
const showDeleteConfirm = ref(false)
const filterToDelete = ref<string | null>(null)

const handleDeleteClick = (id: string, event: Event) => {
  event.stopPropagation() // Prevent row expansion
  filterToDelete.value = id
  showDeleteConfirm.value = true
}

const handleDelete = async () => {
  if (!filterToDelete.value) return

  try {
    await packagesApiClient.delete(`/platform/workers/version-configs/${filterToDelete.value}`)
    showToast('Filter deleted successfully', 'success')
    fetchFilters() // Refresh the list
  } catch (error: any) {
    let errorMessage = 'Failed to delete filter'
    if (error instanceof Error) {
      errorMessage = error.message
    }
    showToast('Failed to delete filter', 'error', [errorMessage])
  } finally {
    showDeleteConfirm.value = false
    filterToDelete.value = null
  }
}

// Typeahead state
const subscriptionSuggestions = ref<string[]>([])
const showSuggestions = ref(false)
const isLoadingSuggestions = ref(false)

// Debounce function
const debounce = (fn: Function, delay: number) => {
  let timeoutId: number | undefined
  return (...args: any[]) => {
    if (timeoutId !== undefined) {
      window.clearTimeout(timeoutId)
    }
    timeoutId = window.setTimeout(() => fn(...args), delay)
  }
}

// Watch subscription input for changes
watch(subscriptionName, (newValue) => {
  if (newValue.trim()) {
    // Show loading state immediately
    showSuggestions.value = true
    isLoadingSuggestions.value = true
    // Delay the API call
    debouncedFetchSuggestions(newValue)

    // If the value exactly matches a suggestion, fetch the filters
    if (subscriptionSuggestions.value.includes(newValue)) {
      fetchFilters()
    }
  } else {
    // Clear suggestions and hide loading for empty input
    subscriptionSuggestions.value = []
    showSuggestions.value = false
    isLoadingSuggestions.value = false

    // Also clear the loaded data and errors
    workerFilters.value = []
    error.value = null
  }
})

// Fetch suggestions
const fetchSuggestions = async (query: string) => {
  if (!query.trim()) {
    subscriptionSuggestions.value = []
    isLoadingSuggestions.value = false
    return
  }

  try {
    const response = await packagesApiClient.get<WorkerFilter[]>(
      `/platform/workers/version-configs?subscription=${encodeURIComponent(query)}`
    )
    // Extract unique subscription names from the response
    const uniqueSubscriptions = [...new Set(response.map((item) => item.subscription))].filter(
      Boolean
    )
    subscriptionSuggestions.value = uniqueSubscriptions
  } catch (error) {
    subscriptionSuggestions.value = []
  } finally {
    isLoadingSuggestions.value = false
  }
}

// Debounced version of fetchSuggestions with longer delay since we show loading immediately
const debouncedFetchSuggestions = debounce(fetchSuggestions, 500)

const handleSuggestionClick = (suggestion: string) => {
  subscriptionName.value = suggestion
  showSuggestions.value = false
  fetchFilters() // Keep this to load the data when a suggestion is selected
}

// Add blur handler for the input
const handleInputBlur = () => {
  // Small delay to allow click event on suggestions to fire first
  setTimeout(() => {
    showSuggestions.value = false
  }, 200)
}

// Close suggestions when clicking outside
const handleClickOutside = (event: MouseEvent) => {
  const target = event.target as HTMLElement
  if (!target.closest('.subscription-input-container')) {
    showSuggestions.value = false
  }
}

onMounted(() => {
  document.addEventListener('click', handleClickOutside)
})

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
})

const handleKeydown = (event: KeyboardEvent) => {
  // If Escape key is pressed
  if (event.key === 'Escape') {
    showSuggestions.value = false
  }
}

// Edit state
const showEditModal = ref(false)
const filterToEdit = ref<WorkerFilter | null>(null)

const handleEditClick = (filter: WorkerFilter, event: Event) => {
  event.stopPropagation() // Prevent row expansion
  filterToEdit.value = filter
  showEditModal.value = true
}

const handleEditSubmit = async (updatedFilter: WorkerFilter) => {
  try {
    await packagesApiClient.put(`/platform/workers/version-configs/${updatedFilter._id}`, {
      subscription: updatedFilter.subscription,
      developerTopic: updatedFilter.developerTopic,
      addedBy: updatedFilter.addedBy,
      matchCondition: updatedFilter.matchCondition,
      isIntegrations: updatedFilter.isIntegrations
    })
    showToast('Filter updated successfully', 'success')
    fetchFilters() // Refresh the list
    showEditModal.value = false
    filterToEdit.value = null
  } catch (error: any) {
    let errorMessage = 'Failed to update filter'
    if (error instanceof Error) {
      errorMessage = error.message
    }
    showToast('Failed to update filter', 'error', [errorMessage])
  }
}

// Clear subscription input
const clearSubscription = () => {
  subscriptionName.value = ''
  workerFilters.value = []
  error.value = null
  subscriptionSuggestions.value = []
  showSuggestions.value = false
  isLoadingSuggestions.value = false
  searchQuery.value = ''

  // Save the cleared state explicitly
  saveFilterState()
}

// Save filter state to localStorage
const saveFilterState = () => {
  const filterState = {
    subscriptionName: subscriptionName.value,
    searchQuery: searchQuery.value
  }
  localStorage.setItem('workers-filter-state', JSON.stringify(filterState))
}

// Load filter state from localStorage
const loadFilterState = () => {
  try {
    const savedState = localStorage.getItem('workers-filter-state')
    if (savedState) {
      const parsedState = JSON.parse(savedState)
      subscriptionName.value = parsedState.subscriptionName || ''
      searchQuery.value = parsedState.searchQuery || ''

      // If we have a subscription, load its data
      if (subscriptionName.value) {
        fetchFilters()
      }
    }
  } catch (err) {
    // Remove console.error to fix build issue - fail silently for saved state loading
  }
}

// Initialize on component mount
onBeforeMount(() => {
  loadFilterState()
})

// Watch for filter changes and save state
watch(
  [subscriptionName, searchQuery],
  () => {
    saveFilterState()
  },
  { deep: true }
)
</script>

<style scoped>
pre {
  font-family:
    ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New',
    monospace;
}

.subscription-input-container {
  isolation: isolate;
}

/* Add styles for the suggestions dropdown */
.subscription-input-container .absolute {
  z-index: 50;
}

/* Disable browser's default autocomplete dropdown */
input:-webkit-autofill,
input:-webkit-autofill:hover,
input:-webkit-autofill:focus {
  -webkit-box-shadow: 0 0 0px 1000px white inset;
  transition: background-color 5000s ease-in-out 0s;
}

/* Hide browser's default autocomplete dropdown */
input::-webkit-calendar-picker-indicator {
  display: none !important;
}
</style>
